import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, ChevronDown } from "lucide-react";
import { CONTACT_INFO } from "@/data/site";
import { SocialLinks } from "@/components/SocialLinks";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

const FOOTER_NAV = [
  { id: "tambours", titre: "Tambours & stages", items: [
    { id: "stages", to: "/offres/stage-fabrication-tambour", label: "Stage de création en groupe" },
    { id: "individuel", to: "/offres/stage-fabrication-tambour#individuel", label: "Stage individuel ou privatisé" },
    { id: "editions", to: "/tambours/editions-limitees", label: "Éditions limitées & Tambours des Lieux" },
    { id: "boutique", to: "/boutique", label: "Boutique — tambours disponibles" },
    { id: "realises", to: "/tambours-realises", label: "Tambours réalisés" },
    { id: "sur-mesure", to: "/tambour-sur-mesure", label: "Faire créer mon tambour" },
    { id: "structures", to: "/tambour-pour-structures", label: "Cercles & stages pour les structures" },
  ] },
  { id: "accompagnements", titre: "Accompagnements & lieux", items: [
    { id: "consultations", to: "/offres/consultations-holistiques", label: "Consultation holistique" },
    { id: "soins", to: "/offres/soins-energetiques", label: "Soins énergétiques à distance" },
    { id: "soin-du-moment", to: "/soin", label: "Soin du moment" },
    { id: "lieux", to: "/offres/lieux-habitats", label: "Géobiologie & harmonisation de lieux" },
    { id: "devis", to: "/contact?sujet=devis-lieu", label: "Demander un devis" },
  ] },
  { id: "activites", titre: "Activités & pratique", items: [
    { id: "agenda", to: "/agenda", label: "Agenda" },
    { id: "wyrd", to: "/le-wyrd", label: "Le Wyrd — formation tambour" },
    { id: "cercles", to: "/offres/cercles-evenements", label: "Cercles & événements" },
    { id: "voyages", to: "/agenda?type=voyages", label: "Voyages en Irlande" },
    { id: "faq", to: "/faq", label: "FAQ" },
    { id: "blog", to: "/blog", label: "Blog" },
    { id: "a-ecouter", to: "/a-ecouter", label: "À écouter" },
    { id: "carnet-des-mondes", to: "/le-carnet-des-mondes", label: "Le Carnet des Mondes" },
    { id: "apropos", to: "/a-propos", label: "Ludovic Isoz" },
    { id: "carte-cadeau", to: "/carte-cadeau", label: "Carte cadeau" },
    { id: "compte", to: "/compte", label: "Mon espace" },
  ] },
];

const FooterCol = ({ col }) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`footer-col-${col.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`footer-col-toggle-${col.id}`} className="w-full flex items-center justify-between md:pointer-events-none">
        <p className="eyebrow mb-0 md:mb-5">{t(col.titre)}</p>
        <ChevronDown className={`w-4 h-4 text-sand/50 md:hidden transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <ul className={`space-y-3 text-sm mt-4 md:mt-0 ${open ? "block" : "hidden md:block"}`}>
        {col.items.map((l) => <li key={l.to}><Link data-testid={`footer-link-${l.id}`} className="link-line hover:text-sand" to={l.to}>{t(l.label)}</Link></li>)}
      </ul>
    </div>
  );
};

export const Footer = () => {
  const { locale, t } = useLocale();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error(locale === "en" ? "Please enter a valid email address" : "Merci d'indiquer un courriel valide");
      return;
    }
    setLoading(true);
    try {
      const res = await api.newsletter(email);
      toast.success(res.message);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || (locale === "en" ? "Subscription failed, please try again." : "L'inscription n'a pas abouti, merci de réessayer."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-earth grain text-sand/85 mt-auto">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 relative z-[2]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4" data-testid="footer-col-marque">
            <div className="flex items-center gap-4 mb-5">
              <img src="/logo.png" alt="Entre Les Mondes" className="w-11 h-11 object-contain" />
              <div>
                <p className="font-eccentric text-3xl text-sand leading-none" data-testid="footer-wordmark">Entre Les Mondes</p>
                <p className="font-serif italic text-xs text-terra tracking-[0.12em] mt-1">Cosmique · Tellurique</p>
              </div>
            </div>
            <address className="not-italic text-sm text-sand/65 leading-relaxed space-y-1" data-testid="footer-coordonnees">
              <p className="text-sand/85">Entre Les Mondes — Ludovic Isoz</p>
              <p>{locale === "en" ? "Studio" : "Atelier"} : Rue Pierre Viret 8, 1350 Orbe, {locale === "en" ? "Switzerland" : "Suisse"}</p>
              <p>E-mail : <a data-testid="footer-email" className="link-line hover:text-sand" href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a></p>
              <p>Téléphone : <a data-testid="footer-tel" className="link-line hover:text-sand" href="tel:+41794606595">+41 79 460 65 95</a></p>
            </address>
            <div className="mt-5"><SocialLinks /></div>
            <form id="newsletter" onSubmit={subscribe} className="mt-8" data-testid="newsletter-form">
              <label htmlFor="newsletter-email" className="eyebrow block mb-3">{locale === "en" ? "Receive the next Carnets des Mondes" : "Recevoir les prochains Carnets des Mondes"}</label>
              <p className="text-sm text-sand/60 leading-relaxed mb-3">{locale === "en" ? "Next issues, discoveries and news from Entre Les Mondes, directly in your inbox." : "Les prochains numéros, mes découvertes et les nouvelles d’Entre Les Mondes, directement dans votre boîte mail."}</p>
              <div className="flex border border-sand/25 rounded-full overflow-hidden focus-within:border-terra transition-colors">
                <input
                  id="newsletter-email"
                  data-testid="newsletter-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@courriel.ch"
                  className="flex-1 bg-transparent px-5 py-3 text-sm placeholder:text-sand/40 focus:outline-none"
                />
                <button type="submit" disabled={loading} data-testid="newsletter-submit" aria-label={locale === "en" ? "Subscribe" : "S'abonner"} className="px-5 bg-terra text-earth hover:bg-sand hover:text-earth transition-colors duration-300 disabled:opacity-60">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
          <nav className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-6 sm:gap-y-10" aria-label="Plan du site" data-testid="footer-nav">
            {FOOTER_NAV.map((col) => <FooterCol key={col.id} col={col} />)}
          </nav>
        </div>
        <div className="mt-16 pt-8 border-t border-sand/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-sand/45 font-mono tracking-wider">
          <span>© 2026 Entre Les Mondes · Orbe (VD) · {locale === "en" ? "Made in Switzerland" : "Fabriqué en Suisse"}</span>
          <span className="flex flex-wrap gap-x-6 gap-y-2">
            <Link data-testid="footer-legal-mentions" className="link-line hover:text-sand" to="/mentions-legales">{t("Mentions légales")}</Link>
            <Link data-testid="footer-legal-confidentialite" className="link-line hover:text-sand" to="/confidentialite">{t("Confidentialité")}</Link>
            <Link data-testid="footer-legal-cgv" className="link-line hover:text-sand" to="/cgv">{t("CGV")}</Link>
            <Link data-testid="footer-admin" className="link-line hover:text-sand" to="/admin">{t("Espace privé")}</Link>
          </span>
        </div>
      </div>
    </footer>
  );
};
